import type { SnapshotRow } from '@afterhours/core/published';
import { isMarketOpen } from '@afterhours/core/market-calendar';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import x402 from '../../../../config/x402.json';
import { DataAge } from '../components/DataAge';
import { Mark } from '../components/Mark';
import { Sparkline } from '../components/Sparkline';
import { FREE_ROW_LIMIT, freeSlice, snapshotUrl } from '../data/snapshot';
import { useSnapshot } from '../data/useSnapshot';
import { API_LIVE, BRAND, priceLabel } from '../lib/brand';
import { count, DASH, hhmmUtc, pct, stampUtc, usd, usdCompact } from '../lib/format';

/**
 * The terminal, ported from `apps/web/app/terminal/page.tsx`.
 *
 * Same markup, same ranking unit — one row per POOL, ordered by
 * `pool_activity_score`. Three things differ, all forced by this being a
 * static bundle with no server behind it:
 *
 *  1. The rows come from the published snapshot, not a database handle.
 *  2. The free cap is applied here, in the browser. On `apps/web` the cap is
 *     an entitlement check that runs before the rows reach the wire. Here it
 *     cannot be: the published JSON carries every row, because the publisher
 *     does not know who is asking. So the lock is presentation only — see the
 *     note on `freeSlice()`. The real paywall is the x402 API, which is a
 *     server and can refuse.
 *  3. The unlock panel links to the docs instead of quoting a live endpoint.
 *     The paid API has not been deployed yet, and printing a curl command
 *     against a host that does not answer would be a lie told in monospace.
 */

/** The four signal types, in the mockup's nav order. */
const NAV_SIGNALS: { type: string; label: string }[] = [
  { type: 'gap', label: 'Overnight gaps' },
  { type: 'dislocation', label: 'Dislocations' },
  { type: 'whale', label: 'Whale flow' },
  { type: 'launch', label: 'Stock-paired launches' },
];

const FEED_KIND: Record<string, { cls: string; label: string }> = {
  gap: { cls: 'k-gap', label: 'Overnight gap' },
  dislocation: { cls: 'k-dis', label: 'Dislocation' },
  whale: { cls: 'k-whale', label: 'Whale flow' },
  launch: { cls: 'k-meme', label: 'New launch' },
};

/**
 * Sort orders for the free rows. The published ranking is activity; the other
 * three re-order the same rows in the browser — they never reach past the free
 * cap, because the rows past it are not ours to show here. Missing values sort
 * last rather than being treated as zero.
 */
type SortKey = 'activity' | 'premium' | 'volume' | 'liquidity';
const SORTS: { key: SortKey; label: string; value: (r: SnapshotRow) => number | null }[] = [
  { key: 'activity', label: 'Activity', value: (r) => r.score },
  { key: 'premium', label: 'Prem / Disc', value: (r) => (r.premiumPct === null ? null : Math.abs(r.premiumPct)) },
  { key: 'volume', label: '24h vol', value: (r) => r.vol24hUsd },
  { key: 'liquidity', label: 'Pool liq', value: (r) => r.liqUsd },
];

export function sortRows(rows: SnapshotRow[], key: SortKey): SnapshotRow[] {
  if (key === 'activity') return rows;
  const value = SORTS.find((s) => s.key === key)!.value;
  const v = (r: SnapshotRow) => {
    const x = value(r);
    return x === null || !Number.isFinite(x) ? -Infinity : x;
  };
  return [...rows].sort((a, b) => v(b) - v(a));
}

/** A premium only earns a colour once it is big enough to mean something. */
function premClass(p: number | null): string {
  if (p === null || !Number.isFinite(p)) return 'prem dim';
  if (p >= 1) return 'prem up';
  if (p <= -1) return 'prem down';
  return 'prem dim';
}

function Ticker({ row }: { row: SnapshotRow }) {
  return (
    <div className="tkr">
      <span className="sq">{row.symbol.slice(0, 2)}</span>
      <div className="nm">
        <b>{row.symbol}</b>
        {/* The quote tells two pools of one token apart (NVDA ×3 in the desk QA). */}
        <span>
          {row.name
            ? row.quote
              ? `${row.name} · vs ${row.quote}`
              : row.name
            : row.quote
              ? `paired vs ${row.quote}`
              : 'stock token'}
        </span>
      </div>
    </div>
  );
}

export function Row({ row }: { row: SnapshotRow }) {
  return (
    <tr>
      <td>
        <Ticker row={row} />
        <div className="rowlinks">
          <a href={`https://www.geckoterminal.com/robinhood/pools/${row.poolAddress}`} rel="noreferrer noopener" target="_blank">
            chart ↗
          </a>
          {row.fomoUrl ? (
            <a href={row.fomoUrl} rel="noreferrer noopener" target="_blank">
              trade ↗
            </a>
          ) : null}
        </div>
      </td>
      <td>{usd(row.priceUsd)}</td>
      <td className="dim" title={row.refKind === 'chainlink' ? 'Chainlink reference' : 'Last NYSE close'}>
        {usd(row.refUsd ?? row.lastClose)}
        {/*
          The publisher falls back to the stored close when it has no fresh
          Chainlink price for this token. Say so on the row, so a premium
          measured against yesterday's close never passes for a live one.
        */}
        {row.refKind === 'chainlink' ? null : <span className="refkind">prev close</span>}
      </td>
      <td className={premClass(row.premiumPct)}>{pct(row.premiumPct)}</td>
      <td className="spark">
        <Sparkline values={row.spark} />
      </td>
      <td>{usdCompact(row.vol24hUsd)}</td>
      <td>{usdCompact(row.liqUsd)}</td>
      <td className="badge-col">
        <span className={`pill ${row.badge.kind}`}>{row.badge.label}</span>
      </td>
    </tr>
  );
}

const MASK = '••••••';

/**
 * A locked row. Only the ticker is real.
 *
 * The masking is kept even though it protects nothing here, for one reason:
 * this markup is the same markup the paid server will render, and the moment
 * the rows do arrive per-entitlement the component must already be honest. A
 * CSS blur over real numbers would not be.
 */
export function LockedRow({ symbol, first }: { symbol: string; first: boolean }) {
  return (
    <tr className="locked">
      <td>
        <div className="blurwrap">
          <div className="tkr">
            <span className="sq">{symbol.slice(0, 2)}</span>
            <div className="nm">
              <b>{symbol}</b>
              <span>locked</span>
            </div>
          </div>
        </div>
      </td>
      <td>
        <span className="blurwrap">{MASK}</span>
      </td>
      <td>
        <span className="blurwrap dim">{MASK}</span>
      </td>
      <td>
        <span className="blurwrap prem">{MASK}</span>
      </td>
      <td className="spark">
        <span className="blurwrap">{MASK}</span>
      </td>
      <td>
        <span className="blurwrap">{MASK}</span>
      </td>
      <td>
        <span className="blurwrap">{MASK}</span>
      </td>
      <td className="badge-col">
        <span className="blurwrap">
          <span className="pill flat">{MASK}</span>
        </span>
        {first ? (
          <div className="lockbar">
            <a className="lockcta" href="#unlock">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M5.5 7V5.2a2.5 2.5 0 015 0V7"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
                <rect
                  x="3.2"
                  y="7"
                  width="9.6"
                  height="6.4"
                  rx="1.8"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
              </svg>
              Full table via {BRAND.pricing.protocol} · {API_LIVE ? BRAND.pricing.perQuery : 'coming soon'}
            </a>
          </div>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * What a paying caller will get, and where the protocol is written down.
 *
 * `apps/web` prints a runnable curl against its own `/api/snapshot`. This
 * deployment has no API — it is a bucket — so the panel says so and links to
 * the spec and the docs instead of printing a command that would fail. The
 * price and the asset come from `config/x402.json`, the same file the 402 is
 * built from, so the two cannot drift apart.
 */
function UnlockPanel() {
  const price = `$${x402.priceUsd.toFixed(3).replace(/0$/, '')}`;
  const free = snapshotUrl();
  return (
    <div className="unlock" id="unlock">
      <div className="unlock-head">
        <span className="t">Agent API{API_LIVE ? '' : ' · coming soon'}</span>
        <span className="price">
          {price} / query · {x402.assetName} on {x402.networkV1}
        </span>
      </div>

      <p>
        {API_LIVE
          ? `The free tier is this page: the top ${FREE_ROW_LIMIT} pools, refreshed every 30s. `
          : 'Every live pool on this page is free while the paid API is being built. '}
        For bots and agents, every ranked pool, every tracked token and the last 24h of signals
        will be served from{' '}
        <span className="mono">/api/snapshot</span>, metered with{' '}
        <a href="https://x402.org" rel="noreferrer noopener" target="_blank">
          x402
        </a>
        . No account, no key, no subscription — an agent pays per request and the response carries
        the receipt. <b>The paid endpoint is not deployed yet</b>; until it is, the free JSON
        snapshot below is the whole feed.
      </p>

      <div className="unlock-foot">
        {free ? (
          <a href={free} rel="noreferrer noopener" target="_blank">
            Free JSON snapshot
          </a>
        ) : (
          <span className="dim">Free JSON snapshot</span>
        )}
        <span className="sep" />
        <a href="https://github.com/coinbase/x402" rel="noreferrer noopener" target="_blank">
          x402 spec
        </a>
      </div>
    </div>
  );
}

export function Terminal() {
  const { snapshot, now } = useSnapshot();
  const [feedType, setFeedType] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>('activity');

  // A row with no price is a pool nobody has traded recently (or ever): the
  // publisher blanks stale prices, and 170 of 186 rows were dead on 28 Sep.
  const allRows = (snapshot?.rows ?? []).filter((r) => r.priceUsd !== null);
  const refIsLive = allRows.some((r) => r.refKind === 'chainlink');
  // "Live" is only true of the whole column when every row has a live price.
  const refAllLive = refIsLive && allRows.every((r) => r.refKind === 'chainlink');
  const refLabel = refAllLive ? 'live Chainlink price (NYSE open)' : refIsLive ? 'Chainlink where live, else prev close' : 'last NYSE close';
  // The lock only means something once the paid API can sell what it hides:
  // the free JSON carries every row, so a blurred row was theatre (desk QA #5,
  // Kamerin 28 Sep). Until API_LIVE, every live row is shown.
  const { visible, lockedSymbols, lockedCount } = API_LIVE
    ? freeSlice(allRows)
    : { visible: allRows, lockedSymbols: [] as string[], lockedCount: 0 };
  const counts = snapshot?.signalCounts24h ?? {};
  const tre = snapshot?.treasury ?? { total: null, allocations: [], lastSweepTs: null, sweepCount: 0 };
  const allFeed = snapshot?.feed ?? [];
  const feed = feedType ? allFeed.filter((e) => e.type === feedType) : allFeed;
  const feedLabel = NAV_SIGNALS.find((s) => s.type === feedType)?.label ?? null;
  const rows = sortRows(visible, sort);
  const bars = snapshot?.volume12h ?? [];
  const live = snapshot !== null;

  const capStart = bars[0]?.startTs ?? null;
  const capMid = bars[Math.floor(bars.length / 2)]?.startTs ?? null;

  return (
    <div className="app">
      <div className="topbar">
        <div className="tb-left">
          <Link aria-label={`${BRAND.name} home`} className="homelink" to="/">
            <Mark size={24} />
            <span className="wordmark">
              {BRAND.name} <span>terminal</span>
            </span>
          </Link>
          <span className="vsep" />
          <span className="chip">
            <span className={live ? 'dot' : 'dot off'} />
            24/7 · {BRAND.chain}
          </span>
          <span className="chip x402">{priceLabel}</span>
        </div>
        <div className="tb-right">
          <span className="chip">
            {stampUtc(now)} · NYSE {isMarketOpen(new Date(now)) ? 'open' : 'closed'}
          </span>
          {/*
            No wallet is connected and none ever will be here. This bundle has
            no server and the paid tier is bought per request over x402 by an
            agent, not per session in a browser — so the slot states the tier
            instead of showing an address we do not have.
          */}
          <div className="wallet">
            <span className="avatar" />
            <span className="addr">no wallet</span>
            <span className="bal">free tier</span>
          </div>
        </div>
      </div>

      <div className="shell">
        {/* Wrapper is display:contents on desktop, so the sidebar stays a flex child of .shell. */}
        <div className="navscroll">
        <nav>
          {/*
            The counts are the last 24h (`signalCounts24h`); the feed they
            filter reaches further back. Said on the label so the two never
            look like they disagree (QA 29 Sep: "whale 10" filtered to 174).
          */}
          <div className="navlabel">Signals · last 24h</div>
          {NAV_SIGNALS.map((s) => (
            <button
              aria-pressed={feedType === s.type}
              className={feedType === s.type ? 'navitem navbtn active' : 'navitem navbtn'}
              key={s.type}
              onClick={() => setFeedType(feedType === s.type ? null : s.type)}
              title={`Show only ${s.label.toLowerCase()} in the agent feed (count: last 24h)`}
              type="button"
            >
              {s.label}{' '}
              <span aria-label={`${counts[s.type] ?? 0} in the last 24h`} className="count">
                {counts[s.type] ?? 0}
              </span>
            </button>
          ))}

          <div className="navdiv" />
          <div className="navlabel">Desk</div>

          <Link className={feedType ? 'navitem' : 'navitem active'} onClick={() => setFeedType(null)} to="/terminal">
            Stock-token pools <span className="count">{snapshot?.totalPools ?? 0}</span>
          </Link>
          <Link className="navitem" to="/honest-numbers">
            Honest numbers
          </Link>
          {/*
            `apps/web` links this to its own /api/snapshot. There is no API on
            this host, so the entry opens the panel below that says what the
            paid tier is and that it is not deployed yet.
          */}
          <a className="navitem locked" href="#unlock">
            API <span className="count">{BRAND.pricing.protocol}</span>
          </a>

          <div className="navfoot">
            <div className="t">Treasury in stock tokens</div>
            <div className="v">{usd(tre.total, 0)}</div>
            <div className="s">
              {tre.sweepCount > 0 ? `${tre.sweepCount} sweeps recorded` : 'No sweep recorded yet'}
            </div>
          </div>
        </nav>
        </div>

        <main>
          <DataAge />

          <div className="mainhead">
            <div>
              <h1>Stock tokens</h1>
              <div className="subh">
                {count(snapshot?.trackedTokens ?? null)} stock tokens tracked ·{' '}
                {count(allRows.length)} of {count(snapshot?.totalPools ?? null)} pools with a live price ·
                on-chain mid vs {refLabel} ·{' '}
                <b>refresh 30s</b>
              </div>
            </div>
            <div className="filters" role="group" aria-label="Sort rows">
              {SORTS.map((o) => (
                <button
                  aria-pressed={sort === o.key}
                  className={sort === o.key ? 'f on' : 'f'}
                  key={o.key}
                  onClick={() => setSort(o.key)}
                  type="button"
                >
                  {o.label}
                  {sort === o.key ? ' ↓' : ''}
                </button>
              ))}
            </div>
          </div>

          <div className="tablewrap">
            <div className="tablescroll">
            <table>
              <colgroup>
                <col style={{ width: 150 }} />
                <col style={{ width: 106 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 104 }} />
                <col style={{ width: 64 }} />
                <col style={{ width: 104 }} />
                <col style={{ width: 104 }} />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th>Token</th>
                  <th>On-chain</th>
                  <th>{refAllLive ? 'Chainlink' : refIsLive ? 'Chainlink / close' : 'Last close'}</th>
                  <th>Prem / Disc</th>
                  <th>24h</th>
                  <th>24h vol</th>
                  <th>Pool liq</th>
                  <th className="badge-col">Signal</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Row row={r} key={r.poolAddress} />
                ))}
                {lockedSymbols.map((s, i) => (
                  <LockedRow symbol={s} first={i === 0} key={`${s}-${i}`} />
                ))}
                {visible.length === 0 && lockedCount === 0 ? (
                  <tr>
                    <td colSpan={8} className="dim">
                      {live
                        ? 'No pools indexed yet — the scanner writes the first rows on its next pass.'
                        : 'Snapshot unavailable. Nothing is shown rather than something invented.'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            </div>

            <div className="tfoot">
              <span>
                Showing <b>{visible.length}</b> of <b>{count(allRows.length)}</b> pools with a live
                price{API_LIVE ? ` · free tier caps at ${FREE_ROW_LIMIT} rows` : ' · all free'}
              </span>
              <span>
                Reference:{' '}
                {refAllLive
                  ? 'Chainlink, while NYSE is open'
                  : refIsLive
                    ? 'Chainlink while NYSE is open; rows marked prev close use the last stored close'
                    : 'last stored close'}{' '}
                · mid from
                latest pool tick
                {snapshot?.dataAsOf ? ` · ${hhmmUtc(snapshot.dataAsOf)} UTC` : ''}
              </span>
            </div>
          </div>

          <UnlockPanel />

          <div className="statusnote">{BRAND.compliance}</div>
        </main>

        <aside>
          <div className="railhead">
            <span className="t">
              <span className={feed.length > 0 ? 'dot' : 'dot off'} />
              Agent feed{feedLabel ? ` · ${feedLabel}` : ''}
            </span>
            <span className="live">{feed.length > 0 ? 'LIVE · 30S' : 'IDLE'}</span>
          </div>

          <div className="feed">
            {feed.length > 0 ? (
              feed.map((e) => {
                const kind = FEED_KIND[e.type] ?? { cls: 'k-none', label: e.type };
                return (
                  <div className="ev" key={e.id}>
                    <div className="ev-top">
                      <span className={`ev-kind ${kind.cls}`}>{kind.label}</span>
                      <span className="ev-time">{hhmmUtc(e.ts)} UTC</span>
                    </div>
                    <p>
                      {e.symbol ? <b>{e.symbol.toUpperCase()}</b> : null} {e.headline}
                      {e.subline ? <> — {e.subline}</> : null}
                    </p>
                    {e.fomo ? (
                      <a className="evlink" href={e.fomo} rel="noreferrer noopener" target="_blank">
                        trade on fomo ↗
                      </a>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <div className="empty">
                {feedLabel ? (
                  <>
                    <b>No {feedLabel.toLowerCase()} in the feed</b>
                    <button className="linkbtn" onClick={() => setFeedType(null)} type="button">
                      Show all signals
                    </button>
                  </>
                ) : (
                  <>
                    <b>No signals yet</b>
                    The feed fills from the <span className="mono">signals</span> table. Nothing has
                    cleared a configured threshold since the agent started.
                  </>
                )}
              </div>
            )}
          </div>

          <div className="railfoot">
            <div className="t">On-chain volume · last 12h</div>
            <div className="bars">
              {bars.map((b, i) => (
                <span
                  className={i >= bars.length - 4 ? 'bar a' : 'bar'}
                  key={b.startTs}
                  style={{ height: `${Math.max(2, Math.round(b.height * 100))}%` }}
                  title={`${usdCompact(b.usd)} · ${b.trades} trades`}
                />
              ))}
            </div>
            <div className="cap">
              <span>{capStart === null ? DASH : hhmmUtc(capStart)}</span>
              <span>{capMid === null ? DASH : hhmmUtc(capMid)}</span>
              <span>{hhmmUtc(now)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
